import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import Stepper from "@components/common/Stepper/Stepper";
import type { InstituteDetailsData } from "./types/types";
import { useTranslation } from "react-i18next";
import Button from "@components/common/Button/Button";
import { getStepContent, instituteSteps } from "./institute.config";
import Modal from "@components/common/Modal/Modal";

interface InstituteDetailsModalProps {
	open: boolean;
	onClose: () => void;
	data?: InstituteDetailsData;
}

const InstituteDetailsModal: React.FC<InstituteDetailsModalProps> = ({ open, onClose, data }) => {
	const [activeStep, setActiveStep] = useState(0);
	const { t } = useTranslation();

	useEffect(() => {
		if (open) {
			setActiveStep(0);
		}
	}, [open]);

	const handleNext = () => {
		setActiveStep(prevActiveStep => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep(prevActiveStep => prevActiveStep - 1);
	};

	const handleClose = () => {
		setActiveStep(0);
		onClose();
	};

	const footer = (
		<div className="flex justify-end gap-2">
			<Button disabled={activeStep === 0} onClick={handleBack} variant="secondary">
				{t("psdaCommon.common.buttons.back")}
			</Button>
			{activeStep === instituteSteps.length - 1 ? (
				<Button onClick={handleClose} variant="primary" className="text-white">
					{t("psdaCommon.common.buttons.close")}
				</Button>
			) : (
				<Button onClick={handleNext} variant="primary" className="text-white">
					{t("psdaCommon.common.buttons.next")}
				</Button>
			)}
		</div>
	);

	if (!data) {
		return null;
	}

	return (
		<Modal
			isOpen={open}
			onClose={handleClose}
			title={t("components.instituteDetails.title")}
			width="max-w-7xl"
			height="max-h-[90vh]"
			padding="p-8"
			isTooltipEnabled={true}
			tooltipText={t("components.instituteDetails.description")}
			footer={footer}>
			{/* Stepper */}
			<Box className="mb-6 mt-1">
				<Stepper steps={instituteSteps} activeStep={activeStep} variant="main" />
			</Box>

			{/* Step Content */}
			<div className="flex flex-col gap-4">{getStepContent(activeStep, data)}</div>
		</Modal>
	);
};

export default InstituteDetailsModal;
